import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, ArrowLeft, Upload, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_PRODUCT = { name: "", category: "Energía", price: "", priceOriginal: "", priceSubscription: "", paypalButtonId: "", currency: "USD", description: "", ingredients: "", image: "", stock: 10, featured: false };

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form);
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err, "Error al subir imagen"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-gray-300">{label}</Label>
      <div className="flex items-center gap-3 mt-1.5">
        {value && <img src={value} alt="vista previa" className="w-14 h-14 rounded-lg object-cover border border-emerald-500/25" />}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={testid} className="flex items-center gap-2 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-sm px-4 py-2 rounded-full transition-colors duration-200">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Subiendo..." : value ? "Cambiar imagen" : "Subir imagen"}
        </button>
      </div>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="o pega una URL de imagen" className="mt-2 bg-[#050806] border-emerald-500/20 text-xs" />
    </div>
  );
}

function ProductDialog({ open, onClose, product, onSaved }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(product || EMPTY_PRODUCT);
  }, [product, open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, stock: Number(form.stock) || 0 };
      if (form.id) {
        await api.put(`/products/${form.id}`, payload);
      } else {
        await api.post("/products", payload);
      }
      toast.success("Producto guardado");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Error al guardar"));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0D130F] border-emerald-500/25 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{form.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label className="text-gray-300">Nombre</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="product-name-input" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Categoría</Label>
              <Input value={form.category} onChange={set("category")} data-testid="product-category-input" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
            <div>
              <Label className="text-gray-300">Stock</Label>
              <Input type="number" value={form.stock} onChange={set("stock")} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300">Precio oferta</Label>
              <Input value={form.price} onChange={set("price")} data-testid="product-price-input" placeholder="22.36" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
            <div>
              <Label className="text-gray-300">Precio normal (tachado)</Label>
              <Input value={form.priceOriginal || ""} onChange={set("priceOriginal")} data-testid="product-price-original-input" placeholder="28.49" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
            <div>
              <Label className="text-gray-300">Precio suscripción (/mes)</Label>
              <Input value={form.priceSubscription || ""} onChange={set("priceSubscription")} data-testid="product-price-subscription-input" placeholder="19.90" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
            <div>
              <Label className="text-gray-300">Moneda</Label>
              <Input value={form.currency} onChange={set("currency")} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            </div>
          </div>
          <div>
            <Label className="text-gray-300">Descripción</Label>
            <Textarea value={form.description} onChange={set("description")} data-testid="product-description-input" rows={3} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
          </div>
          <div>
            <Label className="text-gray-300">Ingredientes</Label>
            <Input value={form.ingredients} onChange={set("ingredients")} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
          </div>
          <ImageField label="Imagen del producto" value={form.image} onChange={(url) => setForm({ ...form, image: url })} testid="product-image-upload-btn" />
          <div>
            <Label className="text-gray-300">ID de botón PayPal (hosted_button_id)</Label>
            <Input value={form.paypalButtonId || ""} onChange={set("paypalButtonId")} data-testid="product-paypal-input" placeholder="Ej: ABC123XYZ" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
            <p className="text-xs text-gray-500 mt-1.5">Déjalo vacío para mostrar solo el botón de WhatsApp. Al pegar el ID aparece el botón de pago PayPal en la tarjeta.</p>
          </div>
          <button type="submit" disabled={saving} data-testid="product-save-btn" className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-semibold py-2.5 rounded-full transition-colors duration-200">
            {saving ? "Guardando..." : "Guardar producto"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPanel() {
  const [auth, setAuth] = useState(null);
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/content")]);
    setProducts(p.data);
    setContent(c.data);
  };

  useEffect(() => {
    api.get("/auth/me")
      .then(() => { setAuth(true); load(); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const removeProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Producto eliminado");
    load();
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/content", content);
      toast.success("Contenido actualizado");
    } catch (err) {
      toast.error(formatApiError(err, "Error al guardar"));
    } finally {
      setSavingContent(false);
    }
  };

  const setC = (k, v) => setContent({ ...content, [k]: v });
  const setVideo = (i, k, v) => {
    const videos = content.videos.map((vid, idx) => (idx === i ? { ...vid, [k]: v } : vid));
    setC("videos", videos);
  };

  if (!auth || !content) {
    return (
      <div className="min-h-screen bg-[#050806] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050806] text-white" data-testid="admin-panel">
      <header className="border-b border-emerald-500/15 glass-panel sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={content.logoUrl || "/assets/logo.jpg"} alt="logo" className="w-9 h-9 rounded-full object-cover ring-1 ring-emerald-500/40" />
            <span className="font-display font-bold tracking-tight">Panel Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-emerald-400 px-3 py-1.5 transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> Ver página
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-400 px-3 py-1.5 transition-colors duration-200">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <Tabs defaultValue="productos">
          <TabsList className="bg-[#0D130F] border border-emerald-500/15 mb-8">
            <TabsTrigger value="productos" data-testid="tab-products">Productos</TabsTrigger>
            <TabsTrigger value="contenido" data-testid="tab-content">Contenido e imágenes</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
          </TabsList>

          <TabsContent value="productos">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold">Productos ({products.length})</h2>
              <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="admin-add-product-btn" className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
                <Plus className="w-4 h-4" /> Agregar producto
              </button>
            </div>
            <div className="space-y-3">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 rounded-xl border border-emerald-500/15 bg-[#0D130F] p-4" data-testid={`admin-product-row-${p.id}`}>
                  {p.image ? <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-emerald-500/10" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.category} · {p.price ? `$${p.price} ${p.currency}` : "Consultar"} · Stock {p.stock}</p>
                  </div>
                  <button onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`admin-edit-product-${p.id}`} className="p-2 text-gray-400 hover:text-emerald-400 transition-colors duration-200" aria-label="Editar">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => removeProduct(p.id)} data-testid={`admin-delete-product-${p.id}`} className="p-2 text-gray-400 hover:text-red-400 transition-colors duration-200" aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {products.length === 0 && <p className="text-gray-500 text-sm">No hay productos. Agrega el primero.</p>}
            </div>
          </TabsContent>

          <TabsContent value="contenido">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-4 rounded-xl border border-emerald-500/15 bg-[#0D130F] p-6">
                <h3 className="font-display font-bold">General</h3>
                <div>
                  <Label className="text-gray-300">Nombre del negocio</Label>
                  <Input value={content.businessName || ""} onChange={(e) => setC("businessName", e.target.value)} data-testid="content-business-name-input" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                </div>
                <div>
                  <Label className="text-gray-300">Eslogan</Label>
                  <Input value={content.tagline || ""} onChange={(e) => setC("tagline", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">WhatsApp (solo números, con código país)</Label>
                    <Input value={content.whatsapp || ""} onChange={(e) => setC("whatsapp", e.target.value)} data-testid="content-whatsapp-input" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">WhatsApp visible</Label>
                    <Input value={content.whatsappDisplay || ""} onChange={(e) => setC("whatsappDisplay", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">Instagram (URL)</Label>
                    <Input value={content.instagram || ""} onChange={(e) => setC("instagram", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Facebook (URL)</Label>
                    <Input value={content.facebook || ""} onChange={(e) => setC("facebook", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Texto legal del pie de página</Label>
                  <Textarea value={content.footerText || ""} onChange={(e) => setC("footerText", e.target.value)} rows={3} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                </div>
                <ImageField label="Logo del negocio" value={content.logoUrl} onChange={(url) => setC("logoUrl", url)} testid="content-logo-upload-btn" />
              </div>

              <div className="space-y-4 rounded-xl border border-emerald-500/15 bg-[#0D130F] p-6">
                <h3 className="font-display font-bold">Hero (portada)</h3>
                <div>
                  <Label className="text-gray-300">Insignia superior</Label>
                  <Input value={content.heroBadge || ""} onChange={(e) => setC("heroBadge", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-300">Línea 1</Label>
                    <Input value={content.heroLine1 || ""} onChange={(e) => setC("heroLine1", e.target.value)} data-testid="content-hero-line1-input" className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Línea 2 (verde)</Label>
                    <Input value={content.heroLine2 || ""} onChange={(e) => setC("heroLine2", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Línea 3 (dorado)</Label>
                    <Input value={content.heroLine3 || ""} onChange={(e) => setC("heroLine3", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Subtítulo</Label>
                  <Textarea value={content.heroSubtitle || ""} onChange={(e) => setC("heroSubtitle", e.target.value)} rows={3} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                </div>
                <div>
                  <Label className="text-gray-300">Cinta animada (separar con comas)</Label>
                  <Textarea
                    value={(content.marqueeItems || []).join(", ")}
                    onChange={(e) => setC("marqueeItems", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                    rows={2}
                    className="mt-1.5 bg-[#050806] border-emerald-500/20"
                  />
                </div>
              </div>
            </div>
            <button onClick={saveContent} disabled={savingContent} data-testid="content-save-btn" className="mt-6 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-semibold px-8 py-2.5 rounded-full transition-colors duration-200">
              {savingContent ? "Guardando..." : "Guardar contenido"}
            </button>
          </TabsContent>

          <TabsContent value="videos">
            <div className="grid md:grid-cols-2 gap-6">
              {(content.videos || []).map((v, i) => (
                <div key={i} className="space-y-4 rounded-xl border border-emerald-500/15 bg-[#0D130F] p-6" data-testid={`admin-video-editor-${i}`}>
                  <h3 className="font-display font-bold">Video {i + 1}</h3>
                  <video src={v.url} muted loop playsInline className="w-full max-h-56 rounded-lg object-cover border border-emerald-500/20" />
                  <div>
                    <Label className="text-gray-300">Título</Label>
                    <Input value={v.title} onChange={(e) => setVideo(i, "title", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Etiqueta</Label>
                    <Input value={v.caption} onChange={(e) => setVideo(i, "caption", e.target.value)} className="mt-1.5 bg-[#050806] border-emerald-500/20" />
                  </div>
                  <div>
                    <Label className="text-gray-300">URL del video</Label>
                    <Input value={v.url} onChange={(e) => setVideo(i, "url", e.target.value)} data-testid={`admin-video-url-${i}`} className="mt-1.5 bg-[#050806] border-emerald-500/20 text-xs" />
                    <p className="text-xs text-gray-500 mt-1.5">Pega la URL de un video .mp4 (por ejemplo, subido a tu hosting o Drive con enlace directo).</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={saveContent} disabled={savingContent} data-testid="videos-save-btn" className="mt-6 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-semibold px-8 py-2.5 rounded-full transition-colors duration-200">
              {savingContent ? "Guardando..." : "Guardar videos"}
            </button>
          </TabsContent>
        </Tabs>
      </main>

      <ProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} product={editing} onSaved={load} />
    </div>
  );
}
